import {
  useLoadingState,
  useQueryState,
  useSiteContext,
  useSiteTitle,
} from '@dustin-web/microsite-components/src/hooks'
import Head from 'next/head'
import React, { useState } from 'react'
import { FilterPage } from '.'
import { getSortBy, useProductListSettings } from '@dustin-web/product-components/src/product-list'
import { useCategoryPageMetadata } from '../categories'
import { useGraphqlInfinite } from '@dustin-web/microsite-data/src/hooks/use-graphql-infinite'
import {
  FacetParameterInput,
  GetCategoryForFilterPageQuery,
  GetCategoryForFilterPageQueryVariables,
} from '@productsite/src/graphql/generated/graphql-types'
import { GetCategoryForFilterPage } from '@productsite/src/graphql/get-category-for-filter-page.graphql'
import { DigitalDataOrigin } from '@dustin-web/product-components/src/utils/digital-data-types'
import { infinityScrollPageSize } from '../constants'
import { CategoryFilterSchema } from '../schemas/collection-page-schema'
import { uniqueArray } from '../utils/unique-array'

export enum FilterPageType {
    Category = 'category',
    Campaign = 'campaign',
    NewProducts = 'newproducts'
}

export interface FilterPageMetaData {
    type: FilterPageType
    urlPartial: string
    title: string
    description: string
    seoText: string
    digitalDataOrigin: DigitalDataOrigin
    facetsFilterExpression?: string
    showOnlyUrlQuery?: string
}

type CategoryFilterPageProps = {
  categoryId: string
  pageMetaData?: FilterPageMetaData
  predefinedFacets?: FacetParameterInput[]
  initialData?: GetCategoryForFilterPageQuery | null
  hideCategoryNavigationHits?: boolean
}

const stripTrailingSlash = (path: string) => path?.replace(/\/$/, '')

const FilterPageLayout = ({
  categoryId,
  pageMetaData,
  predefinedFacets,
  initialData,
  hideCategoryNavigationHits = false,
}: CategoryFilterPageProps) => {
  const { isLoading } = useLoadingState()

  const { sortBy } = useProductListSettings()
  const { queryState } = useQueryState()
  const [page, setPage] = useState(queryState.page)

  const { activeFilters: facets, searchwithin } = queryState

  const combinedFacets = predefinedFacets ? [...facets, ...predefinedFacets] : facets

  const variables = {
    categoryId: categoryId,
    facets: uniqueArray(combinedFacets),
    page,
    pageSize: infinityScrollPageSize,
    searchPhrase: searchwithin,
    sortBy: getSortBy(sortBy),
  }

  const { data, error, size, setSize, isValidating } = useGraphqlInfinite<
    GetCategoryForFilterPageQuery,
    GetCategoryForFilterPageQueryVariables
  >(GetCategoryForFilterPage, {
    variables,
    swrOptions: {
      initialSize: page,
      revalidateFirstPage: false,
      ...(initialData ? { fallbackData: [initialData] } : {}),
    },
  })
  const category = data && data[0]?.category
  
  const { title, metaDescription } = useCategoryPageMetadata(
    category?.seoTitle ?? null,
    category?.seoMetaDescription ?? null,
    category?.seoLongTitleName ?? category?.name ?? '',
    category?.navigation?.path ?? []
  )

  const { getSiteTitle } = useSiteTitle()
  const { siteUrl } = useSiteContext()
  const { pageSize } = useProductListSettings()
  
  const loading = isLoading || isValidating

  if (!data && loading) {
    return null
  }

  if (error) {
    throw error
  }

  const navigation = category?.navigation
  const productSearch = category?.productSearch

  if (!category || !navigation || !productSearch) {
    return null
  }

  const pathSlug = stripTrailingSlash(category.pathSlug)
  const totalPages = Math.ceil(productSearch.productHits / pageSize)


  const defaultPageMetaData: FilterPageMetaData = {
    type: FilterPageType.Category,
    urlPartial: `/group/${pathSlug}`,
    title: title,
    description: metaDescription,
    seoText: category.seoText ?? '',
    digitalDataOrigin: 'filterPage'
  }

  const metaData = pageMetaData || defaultPageMetaData 

  const canonicalUrl = `${siteUrl}/${metaData.urlPartial.replace(/^\/+/, '')}${page > 1 ? `?page=${page}` : ''}`

  const previousPageUrl = page > 1 ? `${siteUrl}${metaData.urlPartial}${`?page=${page - 1}`}` : null
  const nextPagePath = page < totalPages ? `${metaData.urlPartial}${`?page=${page + 1}`}` : null
  const nextPagePageUrl = nextPagePath ? `${siteUrl}${nextPagePath}` : null

  return (
    <>
      <Head>
        <title>{getSiteTitle(metaData.title)}</title>
        {metaData.description && <meta name="description" content={metaData.description} />}
        <link rel="canonical" href={canonicalUrl} />
        {previousPageUrl && <link rel="prev" href={previousPageUrl} />}
        {nextPagePageUrl && <link rel="next" href={nextPagePageUrl} />}
      </Head>
      <CategoryFilterSchema page={data} />
      <FilterPage
        paginatedResults={data}
        category={category}
        loading={loading}
        pageMetaData={metaData}
        hideCategoryNavigationHits={hideCategoryNavigationHits}
        handleLoadMore={() => {
          setSize(size + 1)
          setPage(size + 1)
        }}
        page={page}
      />
    </>
  )
}

export default FilterPageLayout
